/**
 * EntityRef — universal hover-card + click-through + cohort-highlight
 * for any entity reference rendered inside a primitive.
 *
 * Replaces literal <span>{name}</span> usage anywhere a primitive
 * displays an entity (member / contact / bill / account / district /
 * state / committee / coalition). Behavior:
 *
 *   - Inline span with the rendered text.
 *   - On mouseenter → fetches /api/v1/entity-card?kind=...&id=... and
 *     shows a tooltip with photo + subtitle + facts + click-through.
 *     For districts, also fetches the shape from the mapsmcp proxy and
 *     renders it inline as a small SVG. Cached per (kind, id) for the
 *     session — second hover is instant.
 *   - On click → navigates to the entity's detail page.
 *   - If a cohort is active in the workspace and `id` is in the
 *     cohort's entity_ids, applies a highlight ring (amber dotted) —
 *     unless the workspace's view_preferences.highlights_enabled is
 *     false.
 *
 * Workspace view_preferences (highlights_enabled / hover_cards_enabled)
 * gate the interactive layers without re-composing the panel. Set via
 * the set_view_preferences MCP tool.
 */

import { useEffect, useState } from "react";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { AmbientSignalSlot } from "./ambient-signal-registry.js";
import { Star } from "lucide-react";

export type EntityKind =
  | "member"
  | "contact"
  | "bill"
  | "account"
  | "district"
  | "state"
  | "committee"
  | "coalition";

export interface EntityCard {
  kind: EntityKind;
  id: string;
  title: string;
  subtitle?: string;
  photo_url?: string;
  shape_url?: string;
  facts: Array<{ label: string; value: string }>;
  href: string;
}

export interface ActiveCohort {
  cohort_kind: string;
  label: string | null;
  entity_ids: string[];
  resolved_count: number;
  last_resolved_at: string;
}

export interface ViewPreferences {
  highlights_enabled: boolean;
  rotation_enabled: boolean;
  hover_cards_enabled: boolean;
}

const DEFAULT_PREFS: ViewPreferences = {
  highlights_enabled: true,
  rotation_enabled: true,
  hover_cards_enabled: true,
};

// Hook: returns the active cohort for the workspace if any.
export function useActiveCohort(workspaceId: string | null | undefined) {
  return useQuery<{ cohort: ActiveCohort | null }>({
    queryKey: ["active-cohort", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return { cohort: null };
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/active-cohort`,
        { credentials: "include" },
      );
      if (!res.ok) return { cohort: null };
      return res.json();
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

// Hook: returns the workspace's view-preference toggles. Defaults to
// all-on if the row doesn't exist (back-compat for workspaces created
// before view_preferences shipped).
export function useViewPreferences(workspaceId: string | null | undefined) {
  return useQuery<{ prefs: ViewPreferences }>({
    queryKey: ["view-preferences", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return { prefs: DEFAULT_PREFS };
      const res = await fetch(
        `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/view-preferences`,
        { credentials: "include" },
      );
      if (!res.ok) return { prefs: DEFAULT_PREFS };
      const json = await res.json();
      return { prefs: { ...DEFAULT_PREFS, ...(json.prefs ?? {}) } };
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

// Module-level cache to share entity-card fetches across all instances.
const cardCache = new Map<string, EntityCard>();
const inflight = new Map<string, Promise<EntityCard | null>>();

// Module-level cache for shapes (heavier payload, longer cache).
const shapeCache = new Map<string, unknown>();
const shapeInflight = new Map<string, Promise<unknown | null>>();

async function fetchEntityCard(kind: EntityKind, id: string): Promise<EntityCard | null> {
  const cacheKey = `${kind}:${id}`;
  if (cardCache.has(cacheKey)) return cardCache.get(cacheKey)!;
  if (inflight.has(cacheKey)) return inflight.get(cacheKey)!;
  const p = (async () => {
    const url = `/api/v1/entity-card?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const json = (await res.json()) as EntityCard;
    cardCache.set(cacheKey, json);
    return json;
  })();
  inflight.set(cacheKey, p);
  try {
    return await p;
  } finally {
    inflight.delete(cacheKey);
  }
}

async function fetchShapeGeoJson(url: string): Promise<unknown | null> {
  if (shapeCache.has(url)) return shapeCache.get(url)!;
  if (shapeInflight.has(url)) return shapeInflight.get(url)!;
  const p = (async () => {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const json = await res.json();
    shapeCache.set(url, json);
    return json;
  })();
  shapeInflight.set(url, p);
  try {
    return await p;
  } finally {
    shapeInflight.delete(url);
  }
}

export interface EntityRefProps {
  kind: EntityKind;
  id: string;
  /** Inline display text (e.g. the name). Falls back to id if omitted. */
  name?: string;
  /** Optional className for the inline span. */
  className?: string;
  /** Optional workspace_id for cohort highlight check. If omitted, no highlight. */
  workspace_id?: string | null;
  /** If true, renders without a click-through href (still hoverable). */
  no_link?: boolean;
  /** Hydrated ambient-signal payload for this entity. Shape is host-
   *  defined per entity_type and matched against a renderer registered
   *  via registerAmbientSignalRenderer. Renders at chip density next to
   *  the name. When null/undefined or no renderer is registered, the
   *  slot is invisible — no placeholder, no loading state. */
  ambient_signal?: unknown;
}

export function EntityRef({ kind, id, name, className, workspace_id, no_link, ambient_signal }: EntityRefProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const [card, setCard] = useState<EntityCard | null>(() => cardCache.get(`${kind}:${id}`) ?? null);

  const { data: cohortData } = useActiveCohort(workspace_id ?? null);
  const { data: prefsData } = useViewPreferences(workspace_id ?? null);
  const prefs = prefsData?.prefs ?? DEFAULT_PREFS;

  const inCohortRaw = !!(cohortData?.cohort && cohortData.cohort.entity_ids.includes(id));
  const inCohort = inCohortRaw && prefs.highlights_enabled;

  // Fetch card on first hover (or use cache if already loaded). Skipped
  // entirely when hover_cards_enabled is false.
  useEffect(() => {
    if (!hovered || card || !prefs.hover_cards_enabled) return;
    let cancelled = false;
    fetchEntityCard(kind, id).then((c) => {
      if (!cancelled) setCard(c);
    });
    return () => { cancelled = true; };
  }, [hovered, card, kind, id, prefs.hover_cards_enabled]);

  const display = name ?? id;
  const baseClass = "relative inline-block cursor-pointer hover:underline decoration-stone-300 decoration-dotted underline-offset-2 transition-shadow";
  const cohortHighlight = inCohort
    ? "ring-1 ring-amber-400 ring-offset-1 rounded-sm bg-amber-50/70 px-0.5"
    : "";
  const finalClass = [className ?? "", baseClass, cohortHighlight].filter(Boolean).join(" ");

  const href = card?.href ?? entityHref(kind, id);
  const showTooltip = hovered && prefs.hover_cards_enabled;
  const inner = (
    <span
      className={finalClass}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-entity-kind={kind}
      data-entity-id={id}
      data-in-cohort={inCohort ? "1" : "0"}
    >
      {display}
      {ambient_signal != null && (
        <AmbientSignalSlot
          entity_type={kind}
          entity_id={id}
          ambient_signal={ambient_signal}
          density="chip"
        />
      )}
      {showTooltip && (
        <span
          className="absolute left-0 top-full z-50 mt-1 inline-block w-72 rounded-lg border border-stone-200 bg-white shadow-lg pointer-events-none"
          role="tooltip"
        >
          <EntityRefTooltip card={card} kind={kind} id={id} inCohort={inCohort} cohortLabel={cohortData?.cohort?.label ?? null} />
        </span>
      )}
    </span>
  );

  if (no_link) return inner;
  return (
    <a href={href} className="no-underline text-inherit">
      {inner}
    </a>
  );
}

function EntityRefTooltip({
  card,
  kind,
  id,
  inCohort,
  cohortLabel,
}: {
  card: EntityCard | null;
  kind: EntityKind;
  id: string;
  inCohort: boolean;
  cohortLabel: string | null;
}): JSX.Element {
  // Lazy-fetch shape for districts (separate cache; only when card has shape_url)
  const [shape, setShape] = useState<unknown | null>(null);
  useEffect(() => {
    if (kind !== "district" || !card?.shape_url) return;
    let cancelled = false;
    fetchShapeGeoJson(card.shape_url).then((s) => {
      if (!cancelled) setShape(s);
    });
    return () => { cancelled = true; };
  }, [kind, card?.shape_url]);

  if (!card) {
    return (
      <span className="block px-3 py-2 text-xs text-stone-500 italic">
        Loading {kind} {id}…
      </span>
    );
  }
  return (
    <span className="block p-3 text-stone-900">
      <span className="flex items-start gap-2">
        {card.photo_url && (
          <span className="w-10 h-10 rounded-full bg-stone-100 overflow-hidden flex-shrink-0 flex items-center justify-center">
            <img
              src={card.photo_url}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          </span>
        )}
        {kind === "district" && shape != null && (
          <span className="w-10 h-10 rounded bg-stone-50 overflow-hidden flex-shrink-0 flex items-center justify-center border border-stone-200">
            <DistrictShapeSvg geojson={shape} />
          </span>
        )}
        <span className="block min-w-0 flex-1">
          <span className="block text-sm font-semibold text-stone-900 truncate">{card.title}</span>
          {card.subtitle && (
            <span className="block text-[11px] text-stone-500 truncate">{card.subtitle}</span>
          )}
        </span>
        {inCohort && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-accent/60 text-ink flex-shrink-0 inline-flex items-center gap-0.5">
            <Star size={9} strokeWidth={2.25} aria-hidden /> in cohort
          </span>
        )}
      </span>
      {card.facts.length > 0 && (
        <span className="block mt-2 text-[11px] space-y-0.5">
          {card.facts.slice(0, 4).map((f, i) => (
            <span key={i} className="block">
              <span className="font-semibold text-stone-600">{f.label}:</span>{" "}
              <span className="text-stone-700">{f.value}</span>
            </span>
          ))}
        </span>
      )}
      {inCohort && cohortLabel && (
        <span className="block mt-2 text-[10px] text-amber-700 italic">
          Highlighted by active cohort: {cohortLabel}
        </span>
      )}
    </span>
  );
}

/**
 * Render a GeoJSON Polygon / MultiPolygon as a tiny inline SVG. Used in
 * the EntityRef tooltip for districts. Heuristic auto-fit to the SVG
 * viewport; intentionally simple — this is a 40px thumbnail, not a map.
 */
function DistrictShapeSvg({ geojson }: { geojson: unknown }): JSX.Element | null {
  type Coord = [number, number];
  type Ring = Coord[];
  let polys: Ring[];
  const g = geojson as Record<string, unknown> | null;
  if (!g) return null;
  // Accept FeatureCollection / Feature / Geometry shapes.
  const geom = (
    (g.type === "FeatureCollection" && Array.isArray(g.features) && g.features.length > 0
      ? (g.features[0] as Record<string, unknown>).geometry
      : g.type === "Feature"
        ? g.geometry
        : g) as Record<string, unknown> | null
  );
  if (!geom) return null;
  const t = geom.type as string;
  if (t === "Polygon") {
    // Polygon coordinates = Ring[] (outer ring first).
    const outer = (geom.coordinates as Ring[])[0];
    polys = outer ? [outer] : [];
  } else if (t === "MultiPolygon") {
    // MultiPolygon coordinates = Ring[][] (one Ring[] per polygon).
    polys = (geom.coordinates as Ring[][]).map((p) => p[0]).filter(Boolean);
  } else {
    return null;
  }
  if (polys.length === 0) return null;
  // Compute bbox
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const ring of polys) {
    for (const [x, y] of ring) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (!isFinite(minX)) return null;
  const w = maxX - minX || 1;
  const h = maxY - minY || 1;
  const SIZE = 36;
  const scale = Math.min(SIZE / w, SIZE / h) * 0.9;
  const offsetX = (SIZE - w * scale) / 2;
  const offsetY = (SIZE - h * scale) / 2;
  const proj = (x: number, y: number) => [
    offsetX + (x - minX) * scale,
    // Flip Y so north is up
    SIZE - (offsetY + (y - minY) * scale),
  ];
  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} aria-hidden>
      {polys.map((ring, i) => {
        const path = ring.map(([x, y], j) => {
          const [px, py] = proj(x, y);
          return `${j === 0 ? "M" : "L"}${px.toFixed(1)},${py.toFixed(1)}`;
        }).join(" ") + " Z";
        return <path key={i} d={path} fill="rgb(59 130 246 / 0.15)" stroke="rgb(59 130 246)" strokeWidth="1" />;
      })}
    </svg>
  );
}

/**
 * Standard URL contract — used when an EntityCard hasn't loaded yet
 * but we still want a working click-through. Mirror server-side.
 */
export function entityHref(kind: EntityKind, id: string): string {
  switch (kind) {
    case "member":    return `/app/members/${encodeURIComponent(id)}`;
    case "contact":   return `/app/contacts/${encodeURIComponent(id)}`;
    case "bill":      return `/app/bills/${encodeURIComponent(id)}`;
    case "account":   return `/app/accounts/${encodeURIComponent(id)}`;
    case "district":  return `/app/districts/${encodeURIComponent(id)}`;
    case "state":     return `/app/states/${encodeURIComponent(id)}`;
    case "committee": return `/app/committees/${encodeURIComponent(id)}`;
    case "coalition": return `/app/coalitions/${encodeURIComponent(id)}`;
  }
}
